// src/component/Home/ExpertiseArea/ExpertiseArea.jsx
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

/* ---------- DATA ---------- */
const expertiseAreas = [
  { name: "PLM Products", icon: "fa-solid fa-cogs" },
  { name: "Manufacturing & MES", icon: "fa-solid fa-industry" },
  { name: "Data Migration & Upgrades", icon: "fa-solid fa-database" },
  { name: "CAD & ERP Integration", icon: "fa-solid fa-project-diagram" },
  { name: "App Development", icon: "fa-solid fa-mobile-alt" },
  { name: "Cloud Transitions", icon: "fa-solid fa-cloud" },
  { name: "ODC Setup", icon: "fa-solid fa-building" },
  { name: "Mendix App Development", icon: "fa-solid fa-code" },
];

/* ---------- HOOKS ---------- */
// simple reveal-on-scroll using IntersectionObserver
function useRevealOnScroll(selector = "[data-reveal]") {
  useEffect(() => {
    const els = document.querySelectorAll(selector);
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [selector]);
}

// respect reduced motion for people who prefer less animation
function usePrefersReducedMotion() {
  const ref = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    ref.current = mq.matches;
    const handler = (e) => (ref.current = e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return ref;
}

/* ---------- STYLES ---------- */
const ExpertiseAreaSection = styled.section`
  padding: 50px 20px;
  @media (max-width: 768px) { padding: 40px 15px; }
  @media (max-width: 576px) { padding: 30px 10px; }

  /* reveal animation */
  [data-reveal] {
    opacity: 0;
    transform: translateY(18px);
    transition: opacity 420ms ease, transform 420ms ease;
  }
  [data-reveal].in {
    opacity: 1;
    transform: none;
  }
`;

const Container = styled.div`
  .container { font-family: "Poppins", system-ui; }
`;

const ExpertiseTitle = styled.h4`
  font-size: 3em;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  color: #06334b;
  text-align: left;
  span { color: #07b6f2; }
  @media (max-width: 768px) { font-size: 2rem; }
  @media (max-width: 576px) { font-size: 2em; }
`;

const Paragraph = styled.p`
  font-size: 1.1em;
  color: #777;
  font-family: "Poppins", sans-serif;
  margin-top: 1em; margin-bottom: 2em;
  @media (max-width: 768px) { font-size: 1rem; }
  @media (max-width: 576px) {
    font-size: 1em; line-height: 1.71; text-align: justify;
  }
`;

/* 3D Tilt wrapper */
const TiltWrap = styled.div`
  position: relative;
  border: none;
  border-radius: 15px;
  background: radial-gradient(120% 120% at 0% 0%, #ffffff 0%, #f6f9fc 100%);
  box-shadow: 0 12px 26px rgba(16, 24, 40, 0.08);
  transition: transform 220ms cubic-bezier(.2,.7,.2,1), box-shadow 220ms;
  transform-style: preserve-3d;
  cursor: pointer;
  will-change: transform;
  isolation: isolate;

  &:hover {
    box-shadow: 0 18px 44px rgba(16, 24, 40, 0.16);
  }

  /* glare highlight layer */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    mix-blend-mode: screen;
    transition: background 120ms ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
    transform: none !important;
    &:hover { box-shadow: 0 12px 26px rgba(16, 24, 40, 0.08); }
    &::after { display: none; }
  }
`;

const CardBody = styled.div`
  text-align: center;
  padding: 28px 18px;
`;

const CustomIcon = styled.i`
  font-size: 3rem;
  background-color: #07b6f2;
  border-radius: 50%;
  padding: 20px;
  color: #fff;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 100px; width: 100px;
  transform: translateZ(40px); /* 3D pop */
  @media (max-width: 768px) {
    font-size: 2.5rem; padding: 15px; height: 80px; width: 80px;
  }
  @media (max-width: 576px) {
    font-size: 2rem; padding: 10px; height: 60px; width: 60px;
  }
`;

const ExpertiseH6 = styled.h6`
  margin-top: 1rem;
  text-align: center;
  min-height: 2.4rem;
  display: flex; align-items: center; justify-content: center;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  color: #06334b;
  font-size: 1.1em;
  line-height: 1.5;
  transform: translateZ(22px);
  @media (max-width: 768px) { font-size: 1.05rem; }
  @media (max-width: 576px) {
    font-size: 1.05rem; font-weight: 700; margin-bottom: 10px;
    font-family: "Space Grotesk", sans-serif;
  }
`;

/* ---------- COMPONENTS ---------- */
// a tiny JS-driven tilt effect with glare; no external libs
function TiltCard({ children, maxTilt = 12, scale = 1.03 }) {
  const prefersReduced = usePrefersReducedMotion();
  const ref = useRef(null);

  const onMove = (e) => {
    if (prefersReduced.current || !ref.current) return;
    const el = ref.current;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const rx = -((y - r.height / 2) / (r.height / 2)) * maxTilt;
    const ry = ((x - r.width / 2) / (r.width / 2)) * maxTilt;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`;

    // glare
    const angle = Math.atan2(y - r.height / 2, x - r.width / 2) * (180 / Math.PI) + 180;
    const dist = Math.hypot(x - r.width / 2, y - r.height / 2) / Math.max(r.width, r.height);
    el.style.setProperty(
      "--glare",
      `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`
    );
    el.style.setProperty("--glare-raw", angle);
    el.style.setProperty("--glare-intensity", String(1 - dist));
    el.style.setProperty("--glare-bg", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`);
    el.style.setProperty("--glare-opacity", String(0.9 * (1 - dist)));
    el.style.setProperty("--glare-clip", "inherit");
    el.style.setProperty("--glare-aspect", "inherit");
    el.style.setProperty("--glare-color", "inherit");
    el.style.setProperty("--glare-radius", "inherit");
    el.style.setProperty("--glare-transition", "background 120ms ease-out");
    el.style.setProperty("--glare-background", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`);
    el.style.setProperty("--glare-bg2", `radial-gradient(400px 200px at ${x}px ${y}px, rgba(255,255,255,.05), transparent 60%)`);
    el.style.setProperty("--glare-bg3", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.12 * (1 - dist)}) 0%, transparent 50%)`);

    // apply glare through ::after
    el.style.setProperty("--glare-gradient", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`);
    el.style.setProperty("--glare-alpha", `${0.18 * (1 - dist)}`);
    el.style.setProperty("--glare-angle", `${angle}deg`);
    el.style.setProperty("--glare-distance", `${dist}`);
    el.style.setProperty("--glare-strength", `${(1 - dist) * 0.18}`);
    el.style.setProperty("--glare-opacity-final", `${(1 - dist) * 0.5}`);
    el.style.setProperty("--glare-transform", `translateZ(80px)`);
    el.style.setProperty("--glare-blend", `screen`);
    el.style.setProperty("--glare-radius2", `inherit`);
    el.style.setProperty("--glare-bg-final", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`);

    el.style.setProperty("--after-bg", `var(--glare-bg-final)`);
    el.style.setProperty("--after-transition", `background 120ms ease-out`);
    el.style.setProperty("--after-mix", `screen`);
    el.style.setProperty("--after-radius", `inherit`);
    el.style.setProperty("--after-inset", `0`);
    el.style.setProperty("--after-pointer", `none`);
    el.style.setProperty("--after-pos", `absolute`);
    el.style.setProperty("--after-content", `""`);
    el.style.setProperty("--after-display", `block`);

    el.style.setProperty("--glow", `0 22px 54px rgba(16,24,40,.12)`);
    el.style.setProperty("--lift", `0 18px 44px rgba(16,24,40,.16)`);
    el.style.setProperty("--tilt-scale", scale);
    el.style.setProperty("--tilt-rot-x", `${rx}deg`);
    el.style.setProperty("--tilt-rot-y", `${ry}deg`);

    el.style.setProperty("--glare-bg-css", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`);
    el.style.setProperty("--glare-opacity-css", `${0.9 * (1 - dist)}`);

    // set ::after background via inline style on element
    el.style.setProperty("--tilt-glare", `conic-gradient(from ${angle}deg, rgba(255,255,255,${0.18 * (1 - dist)}) 0%, rgba(255,255,255,0) 60%)`);
    el.style.setProperty("--tilt-transition", `background 120ms ease-out`);
    el.style.setProperty("--tilt-after", 1);
    el.style.setProperty("--tilt-shadow", `0 18px 44px rgba(16,24,40,.16)`);
    el.style.setProperty("--tilt-persp", `900px`);
    el.style.setProperty("--tilt-will-change", `transform`);
    el.style.setProperty("--tilt-transform-style", `preserve-3d`);
  };

  const onLeave = () => {
    if (!ref.current) return;
    ref.current.style.transform = "";
    ref.current.style.setProperty("--tilt-glare", "transparent");
  };

  return (
    <TiltWrap
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onMouseEnter={onMove}
      style={{ position: "relative" }}
    >
      {/* ::after glare via CSS var */}
      <style>{`
        .tilt-glare-after::after{
          content:"";
          position:absolute;inset:0;border-radius:inherit;pointer-events:none;
          mix-blend-mode:screen;transition:background 120ms ease-out;
          background: var(--tilt-glare, transparent);
        }
      `}</style>
      <div className="tilt-glare-after">{/* wrapper so ::after can work via CSS */}{children}</div>
    </TiltWrap>
  );
}

/* ---------- MAIN ---------- */
const ExpertiseArea = () => {
  useRevealOnScroll();

  return (
    <ExpertiseAreaSection className="expertisearea">
      <Container className="container" id="icon-grid">
        <ExpertiseTitle className="text-left">
          Our Expertise <span>Areas</span>
        </ExpertiseTitle>
        <Paragraph className="text-left paragraph">
          Our team of experts has a wide range of expertise in various fields. We specialize in the following areas:
        </Paragraph>

        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4 justify-content-center">
          {expertiseAreas.map((area, i) => (
            <div className="col" key={area.name} data-reveal>
              <TiltCard>
                <CardBody className="d-flex flex-column align-items-center justify-content-center">
                  <CustomIcon className={`${area.icon} mb-3`} aria-hidden="true" />
                  <ExpertiseH6 className="mb-0 text-body-emphasis text-center">
                    {area.name}
                  </ExpertiseH6>
                </CardBody>
              </TiltCard>
            </div>
          ))}
        </div>
      </Container>
    </ExpertiseAreaSection>
  );
};

export default ExpertiseArea;
